import { Link } from "react-router-dom";
import { Camera, Users, Briefcase, Award, MapPin, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

export default function About() {
  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <SEO
        url="/about"
        title="About Us"
        description="Videography Cape Town is the Mother City's creative industry directory — connecting videographers, editors, and crew with clients, producers, and brands."
      />

      {/* Hero */}
      <section className="relative py-20 md:py-28 px-4 md:px-8 border-b border-zinc-900">
        <div className="max-w-5xl mx-auto">
          <p className="text-orange-500 uppercase tracking-[0.3em] text-xs mb-4" data-testid="about-kicker">
            About
          </p>
          <h1 className="font-heading text-5xl md:text-7xl leading-[0.95] uppercase">
            Cape Town's <span className="text-orange-500">creative industry,</span><br />
            one directory.
          </h1>
          <p className="text-zinc-400 mt-6 text-lg max-w-3xl leading-relaxed">
            Videography Cape Town is the Mother City's dedicated marketplace for film, video
            and photography professionals. We help clients, producers and brands find the right
            crew — and we help creatives get discovered, hired, and paid.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <p className="text-orange-500 uppercase tracking-[0.3em] text-xs mb-3">Our Mission</p>
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Make Cape Town's film industry easier to work with.</h2>
            <p className="text-zinc-400 leading-relaxed">
              For a city that punches far above its weight in global production, finding the right
              local talent is still frustratingly word-of-mouth. Producers waste hours scrolling
              social media. Freelancers lose gigs to gatekeepers. We fix that with a clean,
              searchable, fair directory — built in Cape Town, for Cape Town.
            </p>
          </div>
          <div>
            <p className="text-orange-500 uppercase tracking-[0.3em] text-xs mb-3">What We Do</p>
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Connect people who make things.</h2>
            <p className="text-zinc-400 leading-relaxed">
              Videographers, cinematographers, editors, sound recordists, DITs, gaffers, grips,
              colorists, animators, drone pilots, voice-over artists, locations, gear rental,
              production services — if it takes a crew, it's here.
            </p>
          </div>
        </div>
      </section>

      {/* Stats-ish */}
      <section className="py-16 px-4 md:px-8 bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Camera, label: "Creative Categories", value: "29+" },
            { icon: MapPin, label: "Cape Town Areas Covered", value: "22+" },
            { icon: Users, label: "Built For", value: "Crew & Clients" },
            { icon: Award, label: "Verified Badges", value: "Yes" },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="border border-zinc-800 p-6" data-testid={`about-stat-${label}`}>
              <Icon className="h-6 w-6 text-orange-500 mb-4" />
              <div className="font-heading text-3xl mb-1">{value}</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <p className="text-orange-500 uppercase tracking-[0.3em] text-xs mb-3">What We Stand For</p>
          <h2 className="font-heading text-3xl md:text-5xl mb-10">Principles, not just policies.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Local first", body: "Every creative on this platform works in or around Cape Town. No scraped profiles, no fake distance listings." },
              { title: "Fair to freelancers", body: "Your Free profile gets a link to your website & socials, real contact details, real discovery. Upgrading is optional and earned through value — not used as a gatekeeper." },
              { title: "Zero-fluff client experience", body: "Filter by skill, location, day rate, availability. See verified badges, portfolios, reviews. Get in touch directly. No bidding wars." },
            ].map((v) => (
              <div key={v.title} className="border border-zinc-800 p-6">
                <h3 className="font-heading text-xl mb-3">{v.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / CTA */}
      <section className="py-20 px-4 md:px-8 border-t border-zinc-900">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-orange-500 uppercase tracking-[0.3em] text-xs mb-3">Get in touch</p>
          <h2 className="font-heading text-4xl md:text-5xl mb-6">Let's build it together.</h2>
          <p className="text-zinc-400 mb-8">
            Partnership, press, bug report, or a great idea — we read everything.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-orange-600 hover:bg-orange-500" data-testid="about-contact-btn">
                <Mail className="h-4 w-4 mr-2" /> Contact Us
              </Button>
            </Link>
            <Link to="/signup">
              <Button variant="outline" className="border-zinc-700">
                <Briefcase className="h-4 w-4 mr-2" /> Create a Profile
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
